import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  RadioButtonGroup,
  Textarea,
  TooltipProvider,
} from '@roger/ui-components';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { createRole, listPermissions } from '../../services/api';
import type {
  PermissionResponse,
  RoleResponse,
} from '../../services/api/types.gen';
import { RBACScope } from '@/services/api/models/RBACScope';
import type { RoleProduct } from '../../constants/products';
import RoleProductsPicker from './RoleProductsPicker';
import { PermissionBadge } from './PermissionBadge';
import { matchesPermissionSearch } from './permissionSearch';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

type Props = {
  onCreated: () => void;
  /** Roles already visible to this agency (own + system), for pre-save slug checks. */
  existingRoles: RoleResponse[];
};

function nameToSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\-_:]/g, '');
}

function isSlugValid(slug: string): boolean {
  return slug.length > 0 && /^[a-z0-9][a-z0-9\-_:]*$/.test(slug);
}

const SCOPE_OPTIONS: { value: RBACScope; label: string }[] = [
  { value: 'agency', label: 'Agency' },
  { value: 'clinic', label: 'Clinic' },
];

const SYSTEM_ROLE_OPTIONS = [
  { value: 'no', label: 'No' },
  { value: 'yes', label: 'Yes' },
];

const INTERNAL_ROLE_OPTIONS = [
  { value: 'no', label: 'No' },
  { value: 'yes', label: 'Yes' },
];

const CreateRoleButton: React.FC<Props> = ({ onCreated, existingRoles }) => {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState<RBACScope>('agency');
  const [isSystem, setIsSystem] = useState('no');
  const [isInternal, setIsInternal] = useState('no');
  const [products, setProducts] = useState<RoleProduct[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  const [allPermissions, setAllPermissions] = useState<PermissionResponse[]>(
    []
  );
  const [permSearch, setPermSearch] = useState('');
  const [permDropdownOpen, setPermDropdownOpen] = useState(false);
  const [selectedPerms, setSelectedPerms] = useState<PermissionResponse[]>([]);

  const permDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      try {
        const res = await listPermissions({
          query: { limit: 500 },
        });
        setAllPermissions(res.data ?? []);
      } catch (error) {
        console.error('Failed to load permissions:', error);
        showError('Failed to Load Permissions', 'Unable to load permissions.');
      }
    };
    load();
  }, [open, showError]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        permDropdownRef.current &&
        !permDropdownRef.current.contains(e.target as Node)
      ) {
        setPermDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNameChange = (value: string) => {
    setName(value);
    if (!slugManuallyEdited) {
      setSlug(nameToSlug(value));
    }
  };

  const handleSlugChange = (value: string) => {
    setSlugManuallyEdited(true);
    setSlug(value);
  };

  const filteredPerms = allPermissions.filter(
    p =>
      !selectedPerms.find(s => s.id === p.id) &&
      matchesPermissionSearch(p, permSearch)
  );

  const addPermission = (perm: PermissionResponse) => {
    setSelectedPerms(prev => [...prev, perm]);
    setPermSearch('');
  };

  const removePermission = (permId: string) => {
    setSelectedPerms(prev => prev.filter(p => p.id !== permId));
  };

  const resetForm = () => {
    setName('');
    setSlug('');
    setSlugManuallyEdited(false);
    setDescription('');
    setScope('agency');
    setIsSystem('no');
    setIsInternal('no');
    setProducts([]);
    setSelectedPerms([]);
    setPermSearch('');
    setPermDropdownOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  // Any role visible in the listing (system or this agency's own) is a genuine
  // conflict: system roles and agency roles may not share a slug, and slugs are
  // unique within an agency. The backend re-checks on save.
  const trimmedSlug = slug.trim();
  const slugConflict = existingRoles.find(role => role.slug === trimmedSlug);
  const slugError =
    slug && !isSlugValid(slug)
      ? 'Slug must be lowercase, start with a letter or number, and only contain letters, numbers, hyphens, underscores, and colons'
      : slugConflict
        ? `This slug is already used by ${
            slugConflict.is_system ? 'a system role' : 'another role'
          }`
        : undefined;

  const isFormValid =
    name.trim() !== '' &&
    trimmedSlug !== '' &&
    isSlugValid(slug) &&
    !slugConflict &&
    description.trim() !== '' &&
    products.length > 0;

  const handleCreate = async () => {
    if (!isFormValid) return;
    setIsCreating(true);
    try {
      await createRole({
        body: {
          name: name.trim(),
          slug: slug.trim(),
          description: description.trim(),
          scope,
          is_system: isSystem === 'yes',
          is_internal: isInternal === 'yes',
          products,
          permission_ids: selectedPerms.map(p => p.id),
        },
        throwOnError: true,
      });
      showSuccess(
        'Role Created',
        `Role "${name.trim()}" was created successfully.`
      );
      onCreated();
      handleClose();
    } catch (error) {
      console.error('Failed to create role:', error);
      showError(
        'Failed to Create Role',
        getBusinessErrorMessage(error) ??
          'Unable to create role. Please try again.'
      );
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <Button variant='purple' size='sm' onClick={() => setOpen(true)}>
        <Plus className='h-4 w-4' />
        New Role
      </Button>

      <Dialog open={open} onOpenChange={isOpen => !isOpen && handleClose()}>
        <DialogContent className='max-w-lg max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Create New Role</DialogTitle>
          </DialogHeader>

          <div className='space-y-5 py-2'>
            {/* Name */}
            <div className='space-y-1.5'>
              <Label htmlFor='role-name'>
                Name <span className='text-red-500'>*</span>
              </Label>
              <Input
                id='role-name'
                placeholder='e.g., Nurse'
                value={name}
                onChange={e => handleNameChange(e.target.value)}
              />
            </div>

            {/* Slug */}
            <div className='space-y-1.5'>
              <Label htmlFor='role-slug'>
                Slug <span className='text-red-500'>*</span>
              </Label>
              <Input
                id='role-slug'
                placeholder='e.g., nurse'
                value={slug}
                onChange={e => handleSlugChange(e.target.value)}
                className={
                  slugError ? 'border-red-500 focus-visible:ring-red-500' : ''
                }
              />
              {slugError && <p className='text-xs text-red-500'>{slugError}</p>}
            </div>

            {/* Description */}
            <div className='space-y-1.5'>
              <Label htmlFor='role-description'>
                Description <span className='text-red-500'>*</span>
              </Label>
              <Textarea
                id='role-description'
                placeholder='Describe what this role grants...'
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
              />
            </div>

            {/* Scope */}
            <div className='space-y-1.5'>
              <Label>
                Scope <span className='text-red-500'>*</span>
              </Label>
              <RadioButtonGroup
                orientation='horizontal'
                value={scope}
                onValueChange={value => setScope(value as RBACScope)}
                options={SCOPE_OPTIONS}
              />
            </div>

            {/* System Role */}
            <div className='space-y-1.5'>
              <Label>
                System Role <span className='text-red-500'>*</span>
              </Label>
              <RadioButtonGroup
                orientation='horizontal'
                value={isSystem}
                onValueChange={setIsSystem}
                options={SYSTEM_ROLE_OPTIONS}
              />
            </div>

            {/* Internal Role */}
            <div className='space-y-1.5'>
              <Label>Internal Role</Label>
              <p className='text-xs text-muted-foreground'>
                Internal roles are only visible to external admins in the invite
                and edit flows.
              </p>
              <RadioButtonGroup
                orientation='horizontal'
                value={isInternal}
                onValueChange={setIsInternal}
                options={INTERNAL_ROLE_OPTIONS}
              />
            </div>

            <RoleProductsPicker
              idPrefix='role-product'
              value={products}
              onChange={setProducts}
            />

            {/* Permissions */}
            <div className='space-y-1.5'>
              <Label>Permissions</Label>
              <div className='relative' ref={permDropdownRef}>
                <div className='relative'>
                  <Input
                    placeholder='Search permissions...'
                    value={permSearch}
                    onChange={e => setPermSearch(e.target.value)}
                    onFocus={() => setPermDropdownOpen(true)}
                  />
                  <ChevronDown className='absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none' />
                </div>
                {permDropdownOpen && filteredPerms.length > 0 && (
                  <div className='absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-md max-h-80 overflow-y-auto'>
                    {filteredPerms.map(perm => (
                      <button
                        key={perm.id}
                        type='button'
                        className='w-full text-left px-3 py-2 text-sm hover:bg-muted/60 flex flex-col gap-0.5'
                        onMouseDown={e => {
                          e.preventDefault();
                          addPermission(perm);
                        }}
                      >
                        <span className='font-medium'>{perm.name}</span>
                        {perm.description && (
                          <span className='text-xs text-muted-foreground line-clamp-2'>
                            {perm.description}
                          </span>
                        )}
                        <span className='text-[11px] font-mono text-muted-foreground/75'>
                          {perm.slug}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {permDropdownOpen &&
                  filteredPerms.length === 0 &&
                  permSearch && (
                    <div className='absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-md'>
                      <p className='px-3 py-2 text-sm text-muted-foreground'>
                        No permissions found.
                      </p>
                    </div>
                  )}
              </div>
              {selectedPerms.length > 0 && (
                <TooltipProvider delayDuration={150}>
                  <div className='flex flex-wrap gap-1.5 pt-1'>
                    {selectedPerms.map(perm => (
                      <PermissionBadge
                        key={perm.id}
                        permission={perm}
                        variant='secondary'
                        onRemove={() => removePermission(perm.id)}
                      />
                    ))}
                  </div>
                </TooltipProvider>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant='outline'
              onClick={handleClose}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              variant='purple'
              onClick={handleCreate}
              disabled={!isFormValid || isCreating}
            >
              {isCreating ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateRoleButton;
